import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { OrganizationType } from 'modules/Common/utils/Utility';
import { getLocale } from './useLocaleStore';
import { getLocaleMeta, DEFAULT_LOCALE } from 'i18n/locales';

export const DECIMAL_CONVENTION = Object.freeze({
  AUTO: 'auto', // follow the UI language — the default for new users
  EUROPEAN: 'european', // 1.234,56
  AMERICAN: 'american', // 1,234.56
});

// Map our convention names to BCP-47 locales for Intl.NumberFormat.
//
// AUTO defers to the active UI language, which is the right answer now that
// the language is a real signal: a Spanish UI should not need a second
// setting to render Spanish separators.
//
// The two explicit values keep their historical borrowed tags — "de-DE" for
// comma-decimal/dot-grouping, "en-US" for the reverse — so a user who chose
// one before this change sees byte-identical output afterwards. They are used
// ONLY for separator behaviour, never for currency symbols, month names or any
// other language-bearing output; the locale supplies those.
//
// Under `en`, AUTO resolves through defaultConventionForOrg rather than
// straight to en-GB. That preserves the reason
// NEW_DECLARANT_EUROPEAN_DEFAULT_CUTOFF exists: an English-speaking declarant
// in the EU still wants 1.234,56.
export const localeForConvention = (convention, localeCode, orgDefault) => {
  if (convention === DECIMAL_CONVENTION.AMERICAN) return 'en-US';
  if (convention === DECIMAL_CONVENTION.EUROPEAN) return 'de-DE';
  const code = localeCode ?? getLocale();
  if (code === DEFAULT_LOCALE && orgDefault) {
    return orgDefault === DECIMAL_CONVENTION.AMERICAN ? 'en-US' : 'de-DE';
  }
  return getLocaleMeta(code).intlTag;
};

// Declarant orgs created on/after this date default to European; everything
// else defaults to American. The cutoff is the ship date of that change, so
// existing declarants (created earlier) are unaffected even if they sign in
// on a fresh device where localStorage hasn't been seeded yet.
//
// Superseded as a DEFAULT by AUTO (new users get AUTO, which reads the
// language). Still consulted by localeForConvention when the language is
// English and therefore carries no separator signal of its own.
export const NEW_DECLARANT_EUROPEAN_DEFAULT_CUTOFF = Date.UTC(2026, 5, 24);

const isNewDeclarant = (orgType, orgCreatedAt) => {
  if (orgType !== OrganizationType.Declarant) return false;
  if (!orgCreatedAt) return false;
  const created = orgCreatedAt instanceof Date ? orgCreatedAt.getTime() : Number(orgCreatedAt);
  if (!Number.isFinite(created)) return false;
  return created >= NEW_DECLARANT_EUROPEAN_DEFAULT_CUTOFF;
};

export const defaultConventionForOrg = (orgType, orgCreatedAt) =>
  isNewDeclarant(orgType, orgCreatedAt) ? DECIMAL_CONVENTION.EUROPEAN : DECIMAL_CONVENTION.AMERICAN;

// The convention is `null` until either the user picks one or the initializer
// resolves a default from the active organization. `initFromOrg` is a no-op
// once a value is set so refreshes and org-switches don't clobber an explicit
// choice.
//
// `orgDefault` records what defaultConventionForOrg said WITHOUT committing the
// user to it, so AUTO can consult it under English while a later language
// switch still takes effect. It is not a user preference and is never shown.
export const useDecimalConventionStore = create(
  persist(
    (set, get) => ({
      convention: null,
      orgDefault: null,
      setConvention: (convention) => set({ convention }),
      initFromOrg: (orgType, orgCreatedAt) => {
        const orgDefault = defaultConventionForOrg(orgType, orgCreatedAt);
        set({ orgDefault });
        if (get().convention) return;
        // New users get AUTO. Existing users already have an explicit
        // 'european'/'american' in localStorage and are never migrated — see
        // D9: silently changing someone's number format is worse than a
        // Spanish UI showing American separators because they once asked for
        // them.
        set({ convention: DECIMAL_CONVENTION.AUTO });
      },
    }),
    {
      name: 'cg.decimalConvention',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

export const getDecimalConvention = () =>
  useDecimalConventionStore.getState().convention ?? DECIMAL_CONVENTION.AUTO;

export const getOrgDefaultConvention = () => useDecimalConventionStore.getState().orgDefault;

// Option list for the picker. The example numbers are GENERATED, not hardcoded,
// so the AUTO row shows what AUTO will actually do in the active language
// instead of a stale illustration.
export const getDecimalConventionOptions = (t) => {
  const sample = (convention) =>
    new Intl.NumberFormat(localeForConvention(convention, undefined, getOrgDefaultConvention()), {
      minimumFractionDigits: 2,
      // Intl's default useGrouping ('auto') silently drops the thousands
      // separator for es-ES/it-IT/pl-PL — see numberFormat.js's formatNumber.
      // Left unset here, the AUTO row's own sample would demonstrate the bug
      // instead of the picker it's meant to preview.
      useGrouping: true,
    }).format(1234.56);

  return [
    {
      value: DECIMAL_CONVENTION.AUTO,
      label: t
        ? t('preferences.decimalConvention.auto', 'Automatic ({{sample}})', {
            sample: sample(DECIMAL_CONVENTION.AUTO),
          })
        : `Automatic (${sample(DECIMAL_CONVENTION.AUTO)})`,
    },
    {
      value: DECIMAL_CONVENTION.EUROPEAN,
      label: t
        ? t('preferences.decimalConvention.european', 'European ({{sample}})', {
            sample: sample(DECIMAL_CONVENTION.EUROPEAN),
          })
        : `European (${sample(DECIMAL_CONVENTION.EUROPEAN)})`,
    },
    {
      value: DECIMAL_CONVENTION.AMERICAN,
      label: t
        ? t('preferences.decimalConvention.american', 'American ({{sample}})', {
            sample: sample(DECIMAL_CONVENTION.AMERICAN),
          })
        : `American (${sample(DECIMAL_CONVENTION.AMERICAN)})`,
    },
  ];
};

/**
 * @deprecated Static labels with hardcoded example numbers. Use
 * getDecimalConventionOptions(t), which renders the examples in the active
 * locale and includes the AUTO row.
 */
export const DECIMAL_CONVENTION_OPTIONS = [
  { value: DECIMAL_CONVENTION.EUROPEAN, label: 'European (1.234,56)' },
  { value: DECIMAL_CONVENTION.AMERICAN, label: 'American (1,234.56)' },
];
